// src/CustomerSurveyForm.jsx
// استبيان تصميم اللوحة للزبون — معالج خطوة-خطوة تستخدمه الموظفات مع الزبون.
// ما إله أي علاقة بنظام الطلبات — يحفظ فقط بمجموعة Firestore "customerSurveys".

import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { db, storage } from "./firebase";
import { collection, addDoc, getDocs, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  PROJECT_TYPES,
  LOCATIONS,
  FEELINGS,
  ART_STYLES,
  COLORS,
  ELEMENTS,
  PAINT_TYPES,
  BOARD_TYPES,
  FRAME_OPTIONS,
} from "./customerSurveyOptions";

/* ============================================================
   مكوّنات فرعية عامة لإعادة الاستخدام بكل خطوات الاستبيان
   ============================================================ */

const OptionCard = ({ label, hex, selected, onClick, thumbnails }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: "pointer",
      border: selected ? "2px solid #6a1b9a" : "1px solid #ddd",
      borderRadius: 3,
      p: 1.5,
      textAlign: "center",
      bgcolor: selected ? "#f3e5f5" : "#fff",
      transition: "all 0.15s ease",
      height: "100%",
      "&:hover": { boxShadow: 2, transform: "translateY(-2px)" },
    }}
  >
    {thumbnails && thumbnails.length > 0 ? (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          justifyContent: "center",
          mb: 1,
        }}
      >
        {thumbnails.slice(0, 4).map((url, i) => (
          <Box
            key={i}
            sx={{
              width: 46,
              height: 46,
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "#f5f5f5",
            }}
          >
            <img
              src={url}
              alt={`${label}-${i}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ))}
      </Box>
    ) : hex ? (
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: "50%",
          bgcolor: hex,
          border: "1px solid #ccc",
          mx: "auto",
          mb: 1,
        }}
      />
    ) : (
      <Typography fontSize={12} color="text.secondary" sx={{ mb: 1 }}>
        لا توجد صور مرجعية بعد
      </Typography>
    )}
    <Typography fontSize={13} fontWeight={selected ? 700 : 500}>
      {label}
    </Typography>
  </Box>
);

const OptionsGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, 1fr)",
        sm: "repeat(3, 1fr)",
        md: "repeat(4, 1fr)",
      },
      gap: 1.5,
      mt: 2,
    }}
  >
    {children}
  </Box>
);

const ImageUploadBox = ({ files, onChange, label }) => (
  <Box sx={{ mt: 2 }}>
    <Button variant="outlined" component="label" fullWidth>
      {files.length > 0 ? `✅ تم اختيار ${files.length} صورة` : label}
      <input
        hidden
        multiple
        type="file"
        accept="image/*"
        onChange={(e) => onChange(Array.from(e.target.files || []))}
      />
    </Button>

    {files.length > 0 && (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
        {files.map((file, index) => {
          const url = URL.createObjectURL(file);
          return (
            <Box
              key={index}
              sx={{
                position: "relative",
                width: 100,
                height: 100,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #ddd",
                background: "#f5f5f5",
              }}
            >
              <img
                src={url}
                alt={`upload-${index}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Button
                size="small"
                color="error"
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  minWidth: 22,
                  height: 22,
                  borderRadius: "50%",
                  p: 0,
                  background: "#fff",
                }}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
              >
                ✕
              </Button>
            </Box>
          );
        })}
      </Box>
    )}
  </Box>
);

/* ============================================================
   المكوّن الرئيسي
   ============================================================ */

const TOTAL_STEPS = 13; // 0: بيانات الزبون, 1-10: الأسئلة, 11: مراجعة, 12: نجاح

const emptyCustomerInfo = {
  name: "",
  phone: "",
  governorate: "",
  projectType: "",
  subtype: "",
};

const CustomerSurveyForm = () => {
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "";

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [referenceImages, setReferenceImages] = useState({});

  const [customerInfo, setCustomerInfo] = useState(emptyCustomerInfo);
  const [q1, setQ1] = useState({ ideaImages: [], ideaText: "" });
  const [q2, setQ2] = useState({ location: "", otherText: "" });
  const [q3, setQ3] = useState({ wallImages: [] });
  const [q4, setQ4] = useState({ feelings: [], otherText: "" });
  const [q5, setQ5] = useState({ styles: [] });
  const [q6, setQ6] = useState({ colors: [], notes: "" });
  const [q7, setQ7] = useState({ wanted: [], unwanted: [], notes: "" });
  const [q8, setQ8] = useState({ paintType: "" });
  const [q9, setQ9] = useState({ boardType: "" });
  const [q10, setQ10] = useState({ frameChoice: "" });

  useEffect(() => {
    const loadReferenceImages = async () => {
      const snap = await getDocs(collection(db, "surveyOptionImages"));
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data().images || [];
      });
      setReferenceImages(map);
    };
    loadReferenceImages();
  }, []);

  const toggleInArray = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const resetForm = () => {
    setCustomerInfo(emptyCustomerInfo);
    setQ1({ ideaImages: [], ideaText: "" });
    setQ2({ location: "", otherText: "" });
    setQ3({ wallImages: [] });
    setQ4({ feelings: [], otherText: "" });
    setQ5({ styles: [] });
    setQ6({ colors: [], notes: "" });
    setQ7({ wanted: [], unwanted: [], notes: "" });
    setQ8({ paintType: "" });
    setQ9({ boardType: "" });
    setQ10({ frameChoice: "" });
    setCurrentStep(0);
  };

  const uploadImages = async (files, folder) => {
    const urls = [];
    for (const file of files) {
      const safeName = file.name.replace(/[^\w.\-]/g, "_");
      const storageRef = ref(storage, `${folder}/${Date.now()}-${safeName}`);
      await uploadBytes(storageRef, file);
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const [ideaImageUrls, wallImageUrls] = await Promise.all([
        uploadImages(q1.ideaImages, "customerSurveys/ideaImages"),
        uploadImages(q3.wallImages, "customerSurveys/wallImages"),
      ]);

      await addDoc(collection(db, "customerSurveys"), {
        customerInfo,
        q1: { ideaImages: ideaImageUrls, ideaText: q1.ideaText },
        q2,
        q3: { wallImages: wallImageUrls },
        q4,
        q5,
        q6,
        q7,
        q8,
        q9,
        q10,
        createdBy: username || "غير معروف",
        createdAt: Timestamp.now(),
      });

      setCurrentStep(TOTAL_STEPS - 1);
    } catch (err) {
      console.error("فشل إرسال الاستبيان:", err);
      alert("❌ حدث خطأ أثناء إرسال الاستبيان");
    } finally {
      setSubmitting(false);
    }
  };

  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 0:
        return (
          customerInfo.name.trim() &&
          customerInfo.phone.trim() &&
          customerInfo.governorate.trim() &&
          customerInfo.projectType &&
          customerInfo.subtype
        );
      case 2:
        return !!q2.location;
      case 9:
        return !!q8.paintType;
      case 10:
        return !!q9.boardType;
      default:
        return true;
    }
  }, [currentStep, customerInfo, q2, q8, q9]);

  const goNext = () => {
    if (currentStep === TOTAL_STEPS - 2) {
      handleSubmit();
      return;
    }
    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  /* ============================================================
     محتوى كل خطوة
     ============================================================ */

  const renderStepTitle = (icon, title) => (
    <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
      {icon} {title}
    </Typography>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            {renderStepTitle("👤", "معلومات العميل")}
            <TextField
              fullWidth
              label="الاسم"
              sx={{ mb: 2 }}
              value={customerInfo.name}
              onChange={(e) =>
                setCustomerInfo((p) => ({ ...p, name: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="رقم الموبايل"
              sx={{ mb: 2 }}
              value={customerInfo.phone}
              onChange={(e) =>
                setCustomerInfo((p) => ({ ...p, phone: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="المحافظة"
              sx={{ mb: 2 }}
              value={customerInfo.governorate}
              onChange={(e) =>
                setCustomerInfo((p) => ({ ...p, governorate: e.target.value }))
              }
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>نوع المشروع</InputLabel>
              <Select
                label="نوع المشروع"
                value={customerInfo.projectType}
                onChange={(e) =>
                  setCustomerInfo((p) => ({
                    ...p,
                    projectType: e.target.value,
                    subtype: "",
                  }))
                }
              >
                {Object.entries(PROJECT_TYPES).map(([key, val]) => (
                  <MenuItem key={key} value={key}>
                    {val.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {customerInfo.projectType && (
              <FormControl fullWidth>
                <InputLabel>التفاصيل</InputLabel>
                <Select
                  label="التفاصيل"
                  value={customerInfo.subtype}
                  onChange={(e) =>
                    setCustomerInfo((p) => ({ ...p, subtype: e.target.value }))
                  }
                >
                  {PROJECT_TYPES[customerInfo.projectType].subtypes.map(
                    (s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            )}
          </>
        );

      case 1:
        return (
          <>
            {renderStepTitle(
              "💡",
              "شنو أفكار اللوحات اللي حاب نصممها إلك؟"
            )}
            <Typography sx={{ mb: 1, color: "text.secondary" }}>
              عندك أي لوحة أو صور حابهة وتريد نصمملك شي مقارب؟
            </Typography>
            <ImageUploadBox
              files={q1.ideaImages}
              onChange={(files) => setQ1((p) => ({ ...p, ideaImages: files }))}
              label="📤 رفع صورة / صور للفكرة"
            />
            <Typography sx={{ mt: 3, mb: 1, color: "text.secondary" }}>
              أو، شنو الفكرة الأساسية أو الرسالة اللي حاب اللوحة توصلها؟
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="اكتب الفكرة هنا..."
              value={q1.ideaText}
              onChange={(e) =>
                setQ1((p) => ({ ...p, ideaText: e.target.value }))
              }
            />
          </>
        );

      case 2:
        return (
          <>
            {renderStepTitle("📍", "أين ستوضع اللوحة؟")}
            <OptionsGrid>
              {LOCATIONS.map((loc) => (
                <OptionCard
                  key={loc}
                  label={loc}
                  selected={q2.location === loc}
                  onClick={() => setQ2((p) => ({ ...p, location: loc }))}
                />
              ))}
            </OptionsGrid>
            {q2.location === "مكان آخر" && (
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="حدد المكان"
                value={q2.otherText}
                onChange={(e) =>
                  setQ2((p) => ({ ...p, otherText: e.target.value }))
                }
              />
            )}
          </>
        );

      case 3:
        return (
          <>
            {renderStepTitle("🖼️", "ممكن صورة للجدار مع الأثاث؟")}
            <ImageUploadBox
              files={q3.wallImages}
              onChange={(files) => setQ3({ wallImages: files })}
              label="📤 رفع صورة / صور للجدار"
            />
          </>
        );

      case 4:
        return (
          <>
            {renderStepTitle(
              "✨",
              "ما هو الشعور الذي تريد أن تمنحه اللوحة للمكان؟"
            )}
            <OptionsGrid>
              {FEELINGS.map((f) => (
                <OptionCard
                  key={f}
                  label={f}
                  selected={q4.feelings.includes(f)}
                  onClick={() =>
                    setQ4((p) => ({
                      ...p,
                      feelings: toggleInArray(p.feelings, f),
                    }))
                  }
                />
              ))}
            </OptionsGrid>
            {q4.feelings.includes("أخرى") && (
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="حدد الشعور"
                value={q4.otherText}
                onChange={(e) =>
                  setQ4((p) => ({ ...p, otherText: e.target.value }))
                }
              />
            )}
          </>
        );

      case 5:
        return (
          <>
            {renderStepTitle("🖌️", "ما هو الأسلوب الفني الذي تفضله؟")}
            <Typography sx={{ mb: 1, color: "text.secondary" }} fontSize={13}>
              يمكن اختيار أكثر من أسلوب
            </Typography>
            <OptionsGrid>
              {ART_STYLES.map((style) => (
                <OptionCard
                  key={style.key}
                  label={style.label}
                  thumbnails={referenceImages[style.key]}
                  selected={q5.styles.includes(style.key)}
                  onClick={() =>
                    setQ5((p) => ({
                      ...p,
                      styles: toggleInArray(p.styles, style.key),
                    }))
                  }
                />
              ))}
            </OptionsGrid>
          </>
        );

      case 6:
        return (
          <>
            {renderStepTitle("🎨", "ما الألوان التي تفضلها؟")}
            <Typography sx={{ mb: 1, color: "text.secondary" }} fontSize={13}>
              يمكن اختيار أكثر من لون
            </Typography>
            <OptionsGrid>
              {COLORS.map((c) => (
                <OptionCard
                  key={c.key}
                  label={c.label}
                  hex={c.hex}
                  thumbnails={referenceImages[c.key]}
                  selected={q6.colors.includes(c.key)}
                  onClick={() =>
                    setQ6((p) => ({
                      ...p,
                      colors: toggleInArray(p.colors, c.key),
                    }))
                  }
                />
              ))}
            </OptionsGrid>
            <TextField
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              label="نفس ألوان الديكور؟ لو حاب تضيف أو تتفادى ألوان معينة، اكتبها هنا"
              value={q6.notes}
              onChange={(e) => setQ6((p) => ({ ...p, notes: e.target.value }))}
            />
          </>
        );

      case 7:
        return (
          <>
            {renderStepTitle(
              "🧩",
              "هل توجد عناصر تريد ظهورها أو عدم ظهورها؟"
            )}
            <Typography fontWeight="bold" sx={{ mb: 1 }}>
              أريد وجود:
            </Typography>
            <OptionsGrid>
              {ELEMENTS.map((el) => (
                <OptionCard
                  key={`wanted-${el}`}
                  label={el}
                  selected={q7.wanted.includes(el)}
                  onClick={() =>
                    setQ7((p) => ({
                      ...p,
                      wanted: toggleInArray(p.wanted, el),
                      unwanted: p.unwanted.filter((x) => x !== el),
                    }))
                  }
                />
              ))}
            </OptionsGrid>

            <Typography fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
              لا أريد وجود:
            </Typography>
            <OptionsGrid>
              {ELEMENTS.map((el) => (
                <OptionCard
                  key={`unwanted-${el}`}
                  label={el}
                  selected={q7.unwanted.includes(el)}
                  onClick={() =>
                    setQ7((p) => ({
                      ...p,
                      unwanted: toggleInArray(p.unwanted, el),
                      wanted: p.wanted.filter((x) => x !== el),
                    }))
                  }
                />
              ))}
            </OptionsGrid>

            <TextField
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              label="عناصر أخرى (مثلاً: خيل، نخيل، خط عربي، امرأة، وجوه، كتابات...)"
              value={q7.notes}
              onChange={(e) => setQ7((p) => ({ ...p, notes: e.target.value }))}
            />
          </>
        );

      case 8:
        return (
          <>
            {renderStepTitle("🖌️", "ما هو نوع الرسم الذي تفضله؟")}
            <OptionsGrid>
              {PAINT_TYPES.map((p) => (
                <OptionCard
                  key={p.key}
                  label={p.label}
                  thumbnails={referenceImages[p.key]}
                  selected={q8.paintType === p.key}
                  onClick={() => setQ8({ paintType: p.key })}
                />
              ))}
            </OptionsGrid>
          </>
        );

      case 9:
        return (
          <>
            {renderStepTitle("🖼️", "ما هو نوع اللوحة الذي ترغبه؟")}
            <OptionsGrid>
              {BOARD_TYPES.map((b) => (
                <OptionCard
                  key={b.key}
                  label={b.label}
                  selected={q9.boardType === b.key}
                  onClick={() => setQ9({ boardType: b.key })}
                />
              ))}
            </OptionsGrid>
          </>
        );

      case 10:
        return (
          <>
            {renderStepTitle("🖼️", "ما هو نوع ولون الإطار الذي تفضله؟")}
            <OptionsGrid>
              {FRAME_OPTIONS.map((f) => (
                <OptionCard
                  key={f.key}
                  label={f.label}
                  thumbnails={referenceImages[f.key]}
                  selected={q10.frameChoice === f.key}
                  onClick={() => setQ10({ frameChoice: f.key })}
                />
              ))}
            </OptionsGrid>
          </>
        );

      case 11:
        return (
          <>
            {renderStepTitle("✅", "مراجعة الاستبيان قبل الإرسال")}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography>👤 {customerInfo.name} — {customerInfo.phone}</Typography>
              <Typography>📍 {customerInfo.governorate}</Typography>
              <Typography>
                🏠 {PROJECT_TYPES[customerInfo.projectType]?.label} — {customerInfo.subtype}
              </Typography>
              <Typography>
                💡 {q1.ideaText || (q1.ideaImages.length ? `${q1.ideaImages.length} صورة مرفوعة` : "—")}
              </Typography>
              <Typography>📍 مكان التعليق: {q2.location === "مكان آخر" ? q2.otherText : q2.location}</Typography>
              <Typography>🖼️ صور الجدار: {q3.wallImages.length || "—"}</Typography>
              <Typography>✨ الشعور المطلوب: {q4.feelings.join("، ") || "—"}</Typography>
              <Typography>
                🖌️ الأسلوب الفني: {ART_STYLES.filter((s) => q5.styles.includes(s.key)).map((s) => s.label).join("، ") || "—"}
              </Typography>
              <Typography>
                🎨 الألوان: {COLORS.filter((c) => q6.colors.includes(c.key)).map((c) => c.label).join("، ") || "—"}
              </Typography>
              <Typography>🧩 أريد وجود: {q7.wanted.join("، ") || "—"}</Typography>
              <Typography>🚫 لا أريد وجود: {q7.unwanted.join("، ") || "—"}</Typography>
              <Typography>
                🖌️ نوع الرسم: {PAINT_TYPES.find((p) => p.key === q8.paintType)?.label || "—"}
              </Typography>
              <Typography>
                🖼️ نوع اللوحة: {BOARD_TYPES.find((b) => b.key === q9.boardType)?.label || "—"}
              </Typography>
              <Typography>
                🖼️ الإطار: {FRAME_OPTIONS.find((f) => f.key === q10.frameChoice)?.label || "—"}
              </Typography>
            </Box>
          </>
        );

      case 12:
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography fontSize={48}>🎉</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
              تم إرسال الاستبيان بنجاح
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              شكرًا! تم حفظ إجابات الزبون بنجاح.
            </Typography>
            <Button variant="contained" onClick={resetForm} sx={{ mr: 1 }}>
              📝 استبيان جديد
            </Button>
            <Button variant="outlined" onClick={() => navigate("/Baneen")}>
              العودة للوحة بنين
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  const isLastQuestionStep = currentStep === TOTAL_STEPS - 2; // خطوة المراجعة
  const isSuccessStep = currentStep === TOTAL_STEPS - 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f3e5f5 0%, #e3f2fd 100%)",
        py: 5,
      }}
      dir="rtl"
    >
      <Container maxWidth="sm">
        {!isSuccessStep && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography fontWeight={900} fontSize={20}>
              🎨 استبيان تصميم اللوحة
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => navigate("/customer-survey/list")}>
                📋 الاستبيانات
              </Button>
              <Button size="small" onClick={() => navigate("/customer-survey/images")}>
                🖼️ إدارة الصور
              </Button>
            </Box>
          </Box>
        )}

        {!isSuccessStep && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={(currentStep / (TOTAL_STEPS - 2)) * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }}>
              خطوة {currentStep + 1} من {TOTAL_STEPS - 1}
            </Typography>
          </Box>
        )}

        <Paper
          elevation={6}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 4,
            minHeight: 420,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1 }}>{renderStep()}</Box>

          {!isSuccessStep && (
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Button
                variant="outlined"
                disabled={currentStep === 0}
                onClick={goBack}
              >
                → الرجوع
              </Button>
              <Button
                variant="contained"
                disabled={!canGoNext || submitting}
                onClick={goNext}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {submitting
                  ? "جارِ الإرسال..."
                  : isLastQuestionStep
                  ? "إرسال الاستبيان ✅"
                  : "التالي ←"}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default CustomerSurveyForm;
